import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { InventoryItem } from '@/supabase/inventory/types/inventory';

export interface DeleteItemDialogHandle {
  open: (item: InventoryItem) => void;
}

interface DeleteItemDialogContainerProps {
  softDeleteItem: (id: number) => Promise<boolean>;
  isSubmitting: boolean;
}

/**
 * 상품 삭제 확인 다이얼로그를 Inventory 페이지에서 분리해 자체 관리한다. 대상 상품은
 * `open(item)` 인자로만 시딩되고 imperative handle은 `open`만 노출 → 부모는 ref 호출만
 * 하므로 다이얼로그 오픈 시 부모가 재렌더되지 않는다.
 *
 * AlertDialogAction/Cancel은 내부적으로 DialogPrimitive.Close라 클릭 시 기본적으로
 * onOpenChange(false)를 자동 호출한다(Radix composeEventHandlers). 삭제 실패 시 "열린 채
 * 유지"(false 반환 계약)를 지키려면 Action 클릭에서 `preventDefault()`로 이 자동 닫힘을
 * 막고, 성공했을 때만 수동으로 `setIsOpen(false)`한다.
 *
 * ⚠️ React.memo로 감싸지 말 것. isSubmitting은 제출 중 버튼 비활성화(중복 제출 가드)를
 *    실시간으로 반영해야 하는 부모 live prop이다. 지금은 매 렌더 원시값이라 memo를 씌워도
 *    정상 반영되지만, 다른 모달 컨테이너 선례(CombinedShippingModalContainer)와 동일하게
 *    "open-state를 내부에 격리한 컨테이너는 항상 memo 금지"로 통일해 향후 prop 안정화
 *    리팩터가 조용히 재렌더를 생략시키는 사고를 막는다.
 */
export const DeleteItemDialogContainer = forwardRef<DeleteItemDialogHandle, DeleteItemDialogContainerProps>(
  ({ softDeleteItem, isSubmitting }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [item, setItem] = useState<InventoryItem | null>(null);
    // 진행 중이던 제출(await)이 끝나기 전에 다이얼로그가 닫혔다가 다른 대상으로
    // 재오픈되면, 그 응답이 새 세션의 상태를 건드리지 않도록 세대를 구분한다.
    const sessionRef = useRef(0);

    const open = useCallback<DeleteItemDialogHandle['open']>((nextItem) => {
      sessionRef.current += 1;
      setItem(nextItem);
      setIsOpen(true);
    }, []);

    useImperativeHandle(ref, () => ({ open }), [open]);

    const handleOpenChange = useCallback((next: boolean) => {
      if (!next) {
        sessionRef.current += 1;
        setItem(null);
      }
      setIsOpen(next);
    }, []);

    const handleConfirm = useCallback(
      async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (!item) return;
        const session = sessionRef.current;
        const success = await softDeleteItem(item.id);
        if (session !== sessionRef.current) return;
        if (success) {
          setItem(null);
          setIsOpen(false);
        }
      },
      [item, softDeleteItem],
    );

    return (
      <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="break-keep">'{item?.name ?? ''}' 삭제</AlertDialogTitle>
            <AlertDialogDescription className="break-keep">
              재고 기록은 남아 있으며, 같은 이름으로 다시 등록하면 복원됩니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>취소</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              disabled={isSubmitting || !item}
              onClick={handleConfirm}
            >
              삭제
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  },
);

DeleteItemDialogContainer.displayName = 'DeleteItemDialogContainer';
